/**
 * Wiederverwendbares Lead-Sheet-Panel.
 *
 * Enthaelt, was Uebe- und Buehnen-Modus gemeinsam haben: die Darstellung des
 * Sheets, die Auto-Scroll-Leiste und die Gesten (Tippen = Start/Pause,
 * Wischen = Songwechsel).
 *
 * Gezeichnet wird nicht hier: das uebernimmt die allgemeine Zeichenebene.
 * Das Panel liefert nur die noetigen Angaben (Anker und Zeilenhoehe), damit
 * Notizen an ihrer Textzeile haengen bleiben.
 */
import type { Song } from '../types.ts';
import { countBars, countChords, parseChordPro } from '../chordpro/parse.ts';
import { renderSheet } from '../chordpro/render.ts';
import {
  MAX_SPEED,
  MIN_SPEED,
  beatsPerBar,
  clampSpeed,
  createAutoScroller,
  estimateSpeed,
} from '../features/autoscroll.ts';
import type { AutoScroller } from '../features/autoscroll.ts';
import { drawsWith } from '../features/drawing/tools.ts';
import { button, h } from './dom.ts';
import { icon } from './icons.ts';

export interface SheetPanelOptions {
  song: Song;
  /** Schriftgroessenfaktor des Sheets. */
  scale: number;
  /** Kopfzeile mit Titel und Metadaten im Sheet anzeigen. */
  showHead: boolean;
  /** Geschwindigkeit, wenn der Song keine eigene hat und keine Schaetzung greift. */
  fallbackSpeed: number;
  /** Nutzer hat die Geschwindigkeit geaendert – zum Speichern am Song. */
  onSpeedChange: (speed: number) => void;
  /** Kurzes Tippen auf das Sheet. */
  onTap?: () => void;
  /** Waagerechtes Wischen. */
  onSwipe?: (direction: 'left' | 'right') => void;
}

export interface SheetPanel {
  /** Aussenelement mit Sheet und Auto-Scroll-Leiste. */
  element: HTMLElement;
  scrollEl: HTMLElement;
  scroller: AutoScroller;
  /** Angaben fuer die Zeichenebene. */
  drawing: {
    host: HTMLElement;
    content: HTMLElement;
    getAnchors: () => HTMLElement[];
    getUnit: () => number;
  };
  setScale(value: number): void;
  destroy(): void;
}

/** Kopfzeile des Sheets aus den Song-Metadaten. */
function sheetHead(song: Song): HTMLElement {
  const meta: HTMLElement[] = [];
  if (song.key) meta.push(h('span', { class: 'badge', text: song.key }));
  if (song.tempo) meta.push(h('span', { text: `${song.tempo} bpm` }));
  if (song.timeSignature) meta.push(h('span', { text: song.timeSignature }));
  if (song.artist) meta.push(h('span', { text: song.artist }));
  return h(
    'div',
    { class: 'sheet__head' },
    h('h2', { class: 'sheet__title', text: song.title || 'Ohne Titel' }),
    meta.length > 0 ? h('div', { class: 'sheet__meta' }, ...meta) : null,
  );
}

/** Fotos des Lead Sheets unter dem Text; null, wenn es keine gibt. */
function sheetPhotos(song: Song): HTMLElement | null {
  const photos = song.photos ?? [];
  if (photos.length === 0) return null;
  return h(
    'div',
    { class: 'sheet-photos' },
    ...photos.map((photo, i) =>
      h('img', {
        class: 'sheet-photos__img',
        attrs: { src: photo.src, alt: `Lead Sheet, Seite ${i + 1}`, draggable: 'false', decoding: 'async' },
      }),
    ),
  );
}

export function createSheetPanel(opts: SheetPanelOptions): SheetPanel {
  const { song } = opts;
  const parsed = parseChordPro(song.body);

  /* ------------------------------------------------- Aufbau des Sheets */

  const sheetBody = h('div', { class: 'sheet-body' });
  const { lineEls } = renderSheet(parsed, sheetBody);

  const stack = h(
    'div',
    { class: 'sheet-stack' },
    h(
      'div',
      { class: 'sheet' },
      opts.showHead ? sheetHead(song) : null,
      sheetBody,
      sheetPhotos(song),
      // Leerraum am Ende, damit die letzte Zeile hochscrollen kann.
      h('div', { class: 'sheet-tail' }),
    ),
  );

  const scrollEl = h('div', { class: 'sheet-scroll' }, stack);

  /* --------------------------------------------------- Auto-Scroll */

  let speed = song.scrollSpeed ?? opts.fallbackSpeed;
  let speedIsEstimate = song.scrollSpeed === null;

  const scroller = createAutoScroller(scrollEl, speed, (running) => {
    paintPlay(running);
    controls.classList.toggle('autoscroll--running', running);
    // Laeuft es, zieht sich die Leiste zurueck, damit sie nichts verdeckt.
    if (running) armIdle();
    else wake();
  });

  /* ------------------------------------------------------ Auto-Scroll-Pille
   *
   * Eine einzige schwebende Pille statt eines Balkens ueber die ganze Breite:
   *   - links der Knopf fuer Start und Pause,
   *   - rechts daneben das Tempo. Ein Tipper darauf klappt den Regler in
   *     DERSELBEN Pille aus – Tempo und Wiedergabe sind eine Sache, kein
   *     zweites Bedienfeld irgendwo am Rand.
   * Sie schwebt ueber dem Sheet, ist durchscheinend und wird beim Laufen
   * ruhig, damit sie keine Zeile verdeckt.
   */

  const playIcon = h('span', { class: 'autoscroll__icon' }, icon('play'));

  const playBtn = h(
    'button',
    {
      class: 'autoscroll__play',
      attrs: { type: 'button', 'aria-pressed': 'false', 'aria-label': 'Auto-Scroll starten' },
      on: { click: () => scroller.toggle() },
    },
    playIcon,
  );

  /** Symbol und Beschriftung zum Zustand. */
  function paintPlay(running: boolean): void {
    playIcon.textContent = '';
    playIcon.append(icon(running ? 'pause' : 'play'));
    playBtn.setAttribute('aria-pressed', String(running));
    playBtn.setAttribute('aria-label', running ? 'Auto-Scroll pausieren' : 'Auto-Scroll starten');
    // Kurzer Impuls beim Umschalten, damit der Wechsel spuerbar ist.
    playBtn.classList.remove('autoscroll__play--pulse');
    void playBtn.offsetWidth;
    playBtn.classList.add('autoscroll__play--pulse');
  }
  paintPlay(false);

  const speedValue = h('span', { class: 'autoscroll__value' });

  /** Der Tempoknopf: zeigt den Wert und klappt den Regler aus. */
  const speedToggle = h(
    'button',
    {
      class: 'autoscroll__speed',
      attrs: { type: 'button', 'aria-expanded': 'false', 'aria-label': 'Tempo' },
      on: { click: () => setOpen(!open) },
    },
    icon('speed'),
    speedValue,
  );

  const slider = h('input', {
    class: 'autoscroll__range',
    attrs: {
      type: 'range',
      min: String(MIN_SPEED),
      max: String(MAX_SPEED),
      step: '1',
      'aria-label': 'Scroll-Geschwindigkeit',
    },
  });

  const drawer = h(
    'div',
    { class: 'autoscroll__drawer', attrs: { hidden: 'hidden' } },
    button('−', () => setSpeed(speed - 4, true), { class: 'autoscroll__step', title: 'Langsamer' }),
    slider,
    button('+', () => setSpeed(speed + 4, true), { class: 'autoscroll__step', title: 'Schneller' }),
  );

  const controls = h(
    'div',
    { class: 'autoscroll', attrs: { role: 'group', 'aria-label': 'Auto-Scroll' } },
    playBtn,
    speedToggle,
    drawer,
  );

  /* ------------------------------------------- Ausklappen und Zurueckziehen */

  let open = false;
  let idleTimer = 0;

  function setOpen(next: boolean): void {
    open = next;
    drawer.hidden = !next;
    controls.classList.toggle('autoscroll--open', next);
    speedToggle.setAttribute('aria-expanded', String(next));
    wake();
    if (!next && scroller.running) armIdle();
  }

  /** Nach kurzer Ruhe wird die Pille dezent – sie soll nichts verdecken. */
  function armIdle(): void {
    clearTimeout(idleTimer);
    idleTimer = window.setTimeout(() => {
      if (!open) controls.classList.add('autoscroll--idle');
    }, 2600);
  }

  function wake(): void {
    clearTimeout(idleTimer);
    controls.classList.remove('autoscroll--idle');
  }

  controls.addEventListener('pointerenter', wake);
  controls.addEventListener('pointerdown', wake);

  function showSpeed(): void {
    slider.value = String(Math.round(speed));
    speedValue.textContent = `${Math.round(speed)}${speedIsEstimate ? ' *' : ''}`;
    const hint = speedIsEstimate
      ? 'Aus Tempo und Sheet-Länge geschätzt. Sobald du den Regler bewegst, wird der Wert am Song gespeichert.'
      : 'Am Song gespeichert';
    speedToggle.title = `${Math.round(speed)} px/s – ${hint}`;
  }

  /** Geschwindigkeit setzen; `fromUser` speichert sie am Song. */
  function setSpeed(value: number, fromUser: boolean): void {
    speed = clampSpeed(value);
    scroller.setSpeed(speed);
    if (fromUser) {
      speedIsEstimate = false;
      opts.onSpeedChange(speed);
    }
    showSpeed();
    wake();
  }

  slider.addEventListener('input', () => setSpeed(Number(slider.value), true));

  /* ------------------------------------------------------ Gesten */

  let startX = 0;
  let startY = 0;
  let startTime = 0;
  let tracking = false;

  /** Der Stift zeichnet, sobald ein Werkzeug aktiv ist – dann keine Geste. */
  function isAppPointer(ev: PointerEvent): boolean {
    return !drawsWith(ev);
  }

  scrollEl.addEventListener(
    'pointerdown',
    (ev) => {
      if (!isAppPointer(ev)) return;
      tracking = true;
      startX = ev.clientX;
      startY = ev.clientY;
      startTime = performance.now();
    },
    { passive: true },
  );

  scrollEl.addEventListener(
    'pointerup',
    (ev) => {
      if (!tracking || !isAppPointer(ev)) return;
      tracking = false;
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const dt = performance.now() - startTime;

      // Waagerechtes Wischen: Songwechsel.
      if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.6) {
        opts.onSwipe?.(dx < 0 ? 'left' : 'right');
        return;
      }
      // Kurzes Tippen ohne nennenswerte Bewegung: Start/Pause.
      if (Math.abs(dx) < 12 && Math.abs(dy) < 12 && dt < 500) opts.onTap?.();
    },
    { passive: true },
  );

  scrollEl.addEventListener('pointercancel', () => {
    tracking = false;
  });

  /* ------------------------------------------------------ Zusammenbau */

  /*
   * Die Pille sitzt in einer eigenen, durchsichtigen Zeile UNTER dem Sheet.
   * Sie schwebt optisch, verdeckt aber nie eine Akkordzeile – der Scroll-
   * bereich endet oberhalb davon.
   */
  const dock = h('div', { class: 'autoscroll-dock' }, controls);
  const element = h('div', { class: 'sheet-view' }, scrollEl, dock);
  element.style.setProperty('--sheet-scale', String(opts.scale));

  /** Zeilenhoehe des Sheets – Einheit fuer die Zeichenebene. */
  function unit(): number {
    const raw = getComputedStyle(element).getPropertyValue('--sheet-rule-step');
    const value = Number.parseFloat(raw);
    return Number.isFinite(value) && value > 4 ? value : 44;
  }

  // Geschwindigkeit erst schaetzen, wenn das Sheet gemessen werden kann.
  requestAnimationFrame(() => {
    if (speedIsEstimate) {
      const distance = scrollEl.scrollHeight - scrollEl.clientHeight;
      // Takte aus dem Raster sind die genaueste Grundlage. Gibt es keines,
      // bleibt die grobe Annahme "ein Akkord entspricht einem Takt".
      const bars = countBars(parsed) || countChords(parsed);
      const guess = estimateSpeed(song.tempo ?? 0, beatsPerBar(song.timeSignature), bars, distance);
      if (guess !== null) speed = guess;
      scroller.setSpeed(speed);
    }
    showSpeed();
  });

  showSpeed();

  return {
    element,
    scrollEl,
    scroller,
    drawing: {
      host: scrollEl,
      content: stack,
      getAnchors: () => lineEls,
      getUnit: unit,
    },
    setScale(value: number) {
      element.style.setProperty('--sheet-scale', String(value));
    },
    destroy() {
      scroller.destroy();
    },
  };
}
